import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";

function themeColorToCss(val) {
  if (val == null || val === "") return undefined;
  const c = String(val).trim();
  return c || undefined;
}

function readSessionUserId() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return "";
    const u = JSON.parse(raw);
    const id = u?.userId ?? u?.userid ?? u?.id;
    if (id == null || id === "") return "";
    return String(id).trim();
  } catch {
    return "";
  }
}

/** Map API / DB row to display columns (supports several backend shapes). */
function normalizeLeaderboardRow(row, index) {
  if (!row || typeof row !== "object") {
    return {
      key: `row-${index}`,
      rank: index + 1,
      userId: "",
      name: "—",
      points: "—",
    };
  }
  const name =
    row.name ??
    row.player_name ??
    row.user_name ??
    row.display_name ??
    (row.email ? String(row.email).split("@")[0] : null) ??
    "—";
  const rawPoints = row.points ?? row.score ?? row.point ?? null;
  let points = "—";
  if (rawPoints !== null && rawPoints !== undefined && rawPoints !== "") {
    const n = Number(rawPoints);
    points = Number.isFinite(n) ? String(n).padStart(2, "0") : String(rawPoints);
  }
  const rankNum = Number(row.rank ?? row.rk);
  const rank = Number.isFinite(rankNum) && rankNum > 0 ? rankNum : index + 1;
  const userId =
    row.userId != null && row.userId !== ""
      ? String(row.userId)
      : row.userid != null && row.userid !== ""
      ? String(row.userid)
      : "";

  return {
    key: String(row.userId ?? row.userid ?? row.rank ?? row.id ?? row.report_id ?? row.timestamp ?? index),
    rank,
    userId,
    name: String(name),
    points,
  };
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus, currentTheme } = useSelector((s) => s.theme);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(() => readSessionUserId());

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    const body = document.body;
    const prev = body.style.getPropertyValue("backdrop-filter");
    body.style.setProperty("backdrop-filter", "blur(9px)");
    return () => {
      if (prev) body.style.setProperty("backdrop-filter", prev);
      else body.style.removeProperty("backdrop-filter");
    };
  }, []);

  useEffect(() => {
    if (!adminToken || !backendBase) {
      setLoading(false);
      setError(!adminToken ? "Not signed in." : "Server URL not configured.");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const uid = readSessionUserId();
      setCurrentUserId(uid);
      try {
        const res = await fetch(`${backendBase}/getLeaderboard`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify(uid ? { userId: uid } : {}),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setRows(data.data.map(normalizeLeaderboardRow));
        } else if (res.ok && data.success && Array.isArray(data.rows)) {
          setRows(data.rows.map(normalizeLeaderboardRow));
        } else {
          setError(data.error || data.message || "Failed to load leaderboard.");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase]);

  const landingTitleColor =
    themeColorToCss(themeData?.landing_page_title_color ?? themeData?.landing_page_title_colour) ||
    "#111111";

  const gameTextColor =
    themeColorToCss(themeData?.game_text_color ?? themeData?.game_text_colour) || "#ffffff";

  return (
    <div className="leaderboard-page">
    
      <div className="leaderboard-page__inner">
      <header
        className="leaderboard-header"
        style={{ "--leaderboard-header-color": gameTextColor }}
      >
            <span className="leaderboard-trophy" aria-hidden="true">
              🏆
            </span>
            <h1 className="leaderboard-title">LEADERBOARD</h1>
            <span className="leaderboard-trophy" aria-hidden="true">
              🏆
            </span>
          </header>
        <div className="leaderboard-glass">
         

          {themeStatus === "loading" && !themeData ? (
            <p className="leaderboard-status">Loading theme…</p>
          ) : null}

          {loading ? (
            <p className="leaderboard-status">Loading scores…</p>
          ) : error ? (
            <p className="leaderboard-status leaderboard-status--error">{error}</p>
          ) : (
            <div className="leaderboard-table-wrap">
              <table
                className="leaderboard-table"
                style={{ "--leaderboard-table-color": landingTitleColor }}
              >
                <thead>
                  <tr>
                    <th className="leaderboard-table__rank">RANK</th>
                    <th className="leaderboard-table__name-col">NAME</th>
                    <th className="leaderboard-table__points">POINTS</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="leaderboard-empty">
                        No scores yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const isCurrent =
                        currentUserId && r.userId && String(r.userId) === String(currentUserId);
                      const isBelowTop5 = r.rank > 5;
                      const rowClass = [
                        isCurrent ? "leaderboard-row--current" : "",
                        isBelowTop5 ? "leaderboard-row--below-top5" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <tr key={`lb-${idx}-${r.rank}-${r.userId || r.name}`} className={rowClass || undefined}>
                          <td className="leaderboard-table__rank">{r.rank}</td>
                          <td className="leaderboard-table__name-col">{r.name}</td>
                          <td className="leaderboard-table__points">{r.points}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
